import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { cardSrc } from './resultParsers';

// reference owl-item width (~46.5px): .ab2-result card img 35px + spacing
const ITEM_W = 46;
// Desktop (user 2026-10-14): 8 cards in view, an arrow moves 5. The reference
// keeps 8 on a narrow desktop window too (945px screenshot), so this starts at
// 768px. Phone keeps the measured count and pages a full view per click.
const DESKTOP_MIN_W = 768;
const DESKTOP_PER_VIEW = 8;
const DESKTOP_STEP = 5;
// Slide time: the reference owl slides in 0.75s (measured on its AB 50 modal,
// AB3Result SLIDE_MS); 0.25s read as a jump (user 2026-10-14).
const SLIDE_MS = 750;

// Owl Carousel 2 replica — exact owl DOM (owl-stage-outer > owl-stage >
// owl-item) + frontend jaisa React paging (owl-prev/owl-next). Package CSS
// src/styles/owl.carousel.css me (asli owl.carousel + theme); nav positioning
// .ab2-result overrides me already thi. AB 50 (AB3Result) bhi yahi use karta hai.
export function AB4Carousel({ cards, indices }) {
  const outerRef = useRef(null);
  const [start, setStart] = useState(0);
  const [perView, setPerView] = useState(8);
  const [itemW, setItemW] = useState(ITEM_W);
  const [step, setStep] = useState(8);

  // Same as AB3Result: owl sizes every item at container / items-per-view,
  // so the item width follows the container (REF 46.525 px desktop, 41.3 px phone).
  useLayoutEffect(() => {
    const measure = () => {
      const w = outerRef.current?.clientWidth || 0;
      if (w <= 0) return;
      const desktop = window.innerWidth >= DESKTOP_MIN_W;
      const pv = desktop ? DESKTOP_PER_VIEW : Math.max(1, Math.round(w / ITEM_W));
      setPerView(pv);
      setItemW(w / pv);
      setStep(desktop ? DESKTOP_STEP : pv);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cards.length]);

  // First visible card; the last view ends on the last card.
  const maxStart = Math.max(0, cards.length - perView);
  const cur = Math.min(start, maxStart);
  const atStart = cur <= 0;
  const atEnd = cur >= maxStart;
  const translateX = -(cur * itemW);
  const stageWidth = cards.length * itemW;

  const prev = useCallback(() => setStart((p) => Math.max(0, Math.min(p, maxStart) - step)), [step, maxStart]);
  const next = useCallback(() => setStart((p) => Math.min(maxStart, p + step)), [step, maxStart]);

  // Finger drag (user 2026-10-14, like the player modal): the row follows the
  // finger and stops on the nearest whole card when lifted (no momentum). `start` can be
  // fractional after a drag; the arrows step from there.
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);
  const onTouchStart = useCallback((e) => {
    drag.current = { x: e.touches[0].clientX, from: cur };
    setDragging(true);
  }, [cur]);
  const onTouchMove = useCallback((e) => {
    const d = drag.current;
    if (!d || !itemW) return;
    const pos = d.from - (e.touches[0].clientX - d.x) / itemW;
    setStart(Math.min(maxStart, Math.max(0, pos)));
  }, [itemW, maxStart]);
  // Released: settle on the nearest whole card, never half a card in view.
  const onTouchEnd = useCallback(() => {
    drag.current = null;
    setDragging(false);
    setStart((v) => Math.min(maxStart, Math.max(0, Math.round(v))));
  }, [maxStart]);
  const firstIdx = Math.floor(cur);

  return (
    <div className="ab-slider owl-carousel owl-theme owl-loaded owl-drag">
      <div
        className="owl-stage-outer"
        ref={outerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{ touchAction: 'pan-y' }}
      >
        <div
          className="owl-stage"
          style={{ transform: `translate3d(${translateX}px, 0px, 0px)`, transition: dragging ? 'none' : `transform ${SLIDE_MS / 1000}s ease`, width: `${stageWidth}px` }}
        >
          {cards.map((code, i) => {
            const active = i >= firstIdx && i < firstIdx + perView;
            return (
              <div key={i} className={`owl-item${active ? ' active' : ''}`} style={{ width: `${itemW}px` }}>
                <div className="casino-result-cards-item item">
                  <div className="text-center">{indices ? indices[i] : i + 1}</div>
                  {' '}
                  <img src={cardSrc(code)} draggable={false} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={`owl-nav${cards.length <= perView ? ' disabled' : ''}`}>
        <button type="button" role="presentation" className={`owl-prev${atStart ? ' disabled' : ''}`} onClick={atStart ? undefined : prev}>
          <span aria-label="Previous">‹</span>
        </button>
        <button type="button" role="presentation" className={`owl-next${atEnd ? ' disabled' : ''}`} onClick={atEnd ? undefined : next}>
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled"></div>
    </div>
  );
}

// Andar Bahar 150 (ab4) result — card string 150 cards. Bahar = even index
// (positions 1,3,5...149), Andar = odd index (positions 2,4,6...150). Do owl
// sliders (ANDAR/BAHAR) position numbers ke saath. Winner desc = winnat ||
// rdesc ("Win"/"Andar"/"Bahar"); winner icon sirf explicit Andar/Bahar pe.
export default function AB4Result({ detail }) {
  const allCards = (detail?.card || '').split(',').map((c) => c.trim()).filter(Boolean);
  const andar = allCards.filter((_, i) => i % 2 === 1);
  const bahar = allCards.filter((_, i) => i % 2 === 0);
  const andarIndices = andar.map((_, i) => i * 2 + 2);
  const baharIndices = bahar.map((_, i) => i * 2 + 1);

  const winnerText = detail?.winnat || detail?.rdesc || '';
  const wl = winnerText.toLowerCase();
  const andarWon = wl.includes('andar');
  const baharWon = wl.includes('bahar');
  const WINNER_ICON = 'https://wver.sprintstaticdata.com/v230/static/front/img/winner.png';

  return (
    <div className="row row5 ab2-result">
      <div className="col-12 col-lg-8">
        <div className="d-flex">
          <div className="mr-3 abj-title">
            <div className="row row5"><div className="col-12"><b>ANDAR</b></div></div>
            {' '}
            <div className="row row5"><div className="col-12"><b>BAHAR</b></div></div>
          </div>
          {' '}
          <div className="col-9">
            <div className="card-inner">
              <div className="row row5">
                <div className="col-10">
                  {andar.length > 0 && <AB4Carousel cards={andar} indices={andarIndices} />}
                </div>
                {' '}
                <div className="col-2">
                  {andarWon && (
                    <div className="casino-result-cards-item">
                      <img src={WINNER_ICON} className="winner-icon" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {' '}
            <div className="card-inner">
              <div className="row row5">
                <div className="col-10">
                  {bahar.length > 0 && <AB4Carousel cards={bahar} indices={baharIndices} />}
                </div>
                {' '}
                <div className="col-2">
                  {baharWon && (
                    <div className="casino-result-cards-item">
                      <img src={WINNER_ICON} className="winner-icon" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {' '}
      <div className="col-12 col-lg-4">
        <div className="casino-result-desc">
          <div className="casino-result-desc-item">
            <div>Winner <span className="float-right">:</span></div>
            {' '}
            <div>{winnerText}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
